import type {
  CoreAssistantMessage,
  CoreToolMessage,
  Message,
  ToolInvocation,
} from 'ai';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import type { ChatMessageRouter } from '@sixdegree-ai/client';
import logger from '@/lib/logger';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

interface ApplicationError extends Error {
  info: string;
  status: number;
}

export const fetcher = async (url: string) => {
  const res = await fetch(url);

  if (!res.ok) {
    const errorInfo = await res.json().catch(() => ({}));
    logger.error(`Fetch error for ${url}:`, {
      status: res.status,
      statusText: res.statusText,
      info: errorInfo,
    });

    const error = new Error(
      `An error occurred while fetching the data from ${url}.`,
    ) as ApplicationError;

    error.info = errorInfo;
    error.status = res.status;

    throw error;
  }

  return res.json();
};

export function generateUUID(): string {
  // Use native crypto.randomUUID() which is compatible with Next.js 16 server components
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function addToolMessageToChat({
  toolMessage,
  messages,
}: {
  toolMessage: CoreToolMessage;
  messages: Array<Message>;
}): Array<Message> {
  return messages.map((message) => {
    if (message.toolInvocations) {
      return {
        ...message,
        toolInvocations: message.toolInvocations.map((toolInvocation) => {
          const toolResult = toolMessage.content.find(
            (tool) => tool.toolCallId === toolInvocation.toolCallId,
          );

          if (toolResult) {
            return {
              ...toolInvocation,
              state: 'result',
              result: toolResult.result,
            };
          }

          return toolInvocation;
        }),
      };
    }

    return message;
  });
}

export function convertToUIMessages(
  messages: Array<ChatMessageRouter>,
): Array<Message> {
  logger.debug('[DEBUG] convertToUIMessages - Converting messages:', {
    messageCount: messages.length,
    messages: messages.map(m => ({
      id: m.id,
      role: m.role,
      contentType: typeof m.content,
      isArray: Array.isArray(m.content),
      content: m.content,
      createdAt: m.createdAt
    }))
  });

  // Sort messages by createdAt to ensure proper chronological order
  const sortedMessages = [...messages].sort((a, b) => {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  logger.debug('[DEBUG] convertToUIMessages - Sorted messages:', {
    sortedOrder: sortedMessages.map(m => ({
      id: m.id,
      role: m.role,
      createdAt: m.createdAt
    }))
  });

  return sortedMessages.reduce((chatMessages: Array<Message>, message) => {
    /*if (message.role === 'tool') {
      return addToolMessageToChat({
        toolMessage: message as CoreToolMessage,
        messages: chatMessages,
      });
    }*/

    let textContent = '';
    let reasoning: string | undefined = undefined;
    const toolInvocations: Array<ToolInvocation> = [];

    if (typeof message.content === 'string') {
      // First, try to parse the content as JSON to see if it's structured content
      let parsedContent;
      try {
        parsedContent = JSON.parse(message.content);
        logger.debug(`[DEBUG] convertToUIMessages - Parsed JSON content for message ${message.id}:`, parsedContent);
      } catch {
        // Not JSON, treat as regular string
        parsedContent = null;
      }

      if (Array.isArray(parsedContent)) {
        // Content was JSON-encoded structured content, process it as array
        for (const content of parsedContent) {
          if (content.type === 'text') {
            textContent += content.text;
          } else if (content.type === 'reasoning') {
            const reasoningText = content.reasoning || content.text;
            logger.debug(`[DEBUG] convertToUIMessages - Found reasoning in JSON content:`, reasoningText);
            reasoning = reasoning ? `${reasoning}\n\n${reasoningText}` : reasoningText;
          }
        }
      } else if (parsedContent && typeof parsedContent === 'object' && typeof parsedContent.text === 'string') {
        // Backend saves assistant messages as {"text": "...", "tool_events": [...]}
        // Keep the full JSON string so message.tsx can parse tool_events later
        logger.debug(`[DEBUG] convertToUIMessages - Keeping full JSON for message ${message.id} with tool_events`);
        textContent = message.content as string; // Keep original JSON string
      } else {
        // Regular string content - use as-is
        logger.debug(`[DEBUG] convertToUIMessages - Processing string content for message ${message.id}`);

        textContent = message.content as string;
        // reasoning remains undefined since we don't extract from asterisks anymore
      }
    } else if (Array.isArray(message.content)) {
      logger.debug(`[DEBUG] convertToUIMessages - Processing array content for message ${message.id}:`, {
        contentItems: message.content.map(c => ({ type: c.type, hasReasoning: c.type === 'reasoning' }))
      });

      for (const content of message.content) {
        if (content.type === 'text') {
          // Add text content as-is (no longer parsing asterisk content as reasoning)
          textContent += content.text;
          /*} else if (content.type === 'tool-call') {
            toolInvocations.push({
              state: 'call',
              toolCallId: content.toolCallId as string,
              toolName: content.toolCall?.name as string,
              args: content.toolCall?.arguments as Record<string, unknown>,
            });
          }*/
        } else if (content.type === 'reasoning') {
          // Handle explicit reasoning content type (using text field since that's what the API supports)
          const reasoningText = (content as any).reasoning || (content as any).text;
          logger.debug(`[DEBUG] convertToUIMessages - Found explicit reasoning content:`, reasoningText);
          reasoning = reasoning ? `${reasoning}\n\n${reasoningText}` : reasoningText;
        }
      }
    }

    const finalMessage = {
      id: message.id as string,
      role: message.role as Message['role'],
      content: textContent,
      reasoning,
      toolInvocations,
    };

    logger.debug(`[DEBUG] convertToUIMessages - Final message for ${message.id}:`, {
      id: finalMessage.id,
      role: finalMessage.role,
      contentLength: finalMessage.content?.length || 0,
      hasReasoning: !!finalMessage.reasoning,
      reasoningLength: finalMessage.reasoning?.length || 0,
      reasoning: finalMessage.reasoning
    });

    chatMessages.push(finalMessage);

    return chatMessages;
  }, []);
}

type ResponseMessageWithoutId = CoreToolMessage | CoreAssistantMessage;
type ResponseMessage = ResponseMessageWithoutId & { id: string };

export function sanitizeUIMessages(messages: Array<Message>): Array<Message> {
  const messagesBySanitizedToolInvocations = messages.map((message) => {
    if (message.role !== 'assistant') return message;

    if (!message.toolInvocations) return message;

    const toolResultIds: Array<string> = [];

    for (const toolInvocation of message.toolInvocations) {
      if (toolInvocation.state === 'result') {
        toolResultIds.push(toolInvocation.toolCallId);
      }
    }

    const sanitizedToolInvocations = message.toolInvocations.filter(
      (toolInvocation) =>
        toolInvocation.state === 'result' ||
        toolResultIds.includes(toolInvocation.toolCallId),
    );

    return {
      ...message,
      toolInvocations: sanitizedToolInvocations,
    };
  });

  return messagesBySanitizedToolInvocations.filter(
    (message) =>
      message.content.length > 0 ||
      (message.toolInvocations && message.toolInvocations.length > 0),
  );
}

export function getMostRecentUserMessage(messages: Array<Message>) {
  const userMessages = messages.filter((message) => message.role === 'user');
  return userMessages.at(-1);
}


/** "AccountHealthAssessment" → "Account Health Assessment". A type name is
 * sometimes the only label a kind has; a reader should not have to parse
 * camel case. Object names are proper names, so every word keeps its
 * capital. Labels that already contain spaces are left alone. */
export function humanizeLabel(label: string): string {
  if (/\s/.test(label) || !/[a-z][A-Z]/.test(label)) return label;
  return label.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}
